import Adapter from '@ember-data/adapter';
import { inject as service } from '@ember/service';
import { isEmpty, isPresent, isNone } from '@ember/utils';
import { assert } from '@ember/debug';
import { all, hash } from 'rsvp';
import { CATALOG_SERVICE_TYPES, CATALOG_SERVICE_QUERY_TYPES } from '@square/dashboard-data/models/catalog/catalog-service-constants';
import Logger from '@square/dashboard-utilities/utils/logger';
import CatalogError, { CatalogErrorType } from '@square/dashboard-data/utils/catalog/catalog-error';
import { underscore } from '@ember/string';
import { recordIdentifierFor } from '@ember-data/store';
import { A } from '@ember/array';
const PUT_BATCH_LIMIT = 1000;
const DELETE_BATCH_LIMIT = 300;
const QUERY_LIMIT = 500;
const DEFAULT_QUERY_PAGE_SIZE = 20;
const FETCH_ALL_DISALLOWED_FOR_TYPE = {
  ITEM: true,
  ITEM_VARIATION: true,
  ITEM_FEE_MEMBERSHIP: true,
  ITEM_ITEM_MODIFIER_LIST_MEMBERSHIP: true,
  ITEM_VARIATION_VENDOR_INFO: true,
  ITEM_IMAGE: true,
  CATEGORY: false,
  DISCOUNT: false,
  PROMO: false,
  PRICING_RULE: false,
  TIME_PERIOD: false,
  PRODUCT_SET: false
};

/*
 * NB/TODO (cclegg, qvu): For some reason if we load objects on the hasMany side before
 * the belongsTo side, the first time that we access the relationship (like item.menuCategory)
 * Ember Data will tell us that we haven't properly loaded the record.
 * This hack sorts the records so that the hasMany objects will always be loaded before the belongsTo
 * objects. Let's revisit once we have a cleaner fix.
 */

const sortObjectPriorities = ['CATEGORY', 'ITEM_IMAGE', 'ITEM', 'COMPOSITION', 'RECIPE', 'ITEM_VARIATION', 'COMPONENT', 'INGREDIENT', 'OBJECT_IMAGE_MEMBERSHIP', 'PRODUCT_SET', 'TIME_PERIOD', 'PRICING_RULE'];

// note(cclegg): There are no public APIs in EmberData for modifying a model's
// state. This workaround uses the store's private `didSaveRecord` function, which
// can handle committing a save operation for new or existing models.
function commitSaveToRecord(store, modelName, record, responseObject) {
  const modelClass = store.modelFor(modelName);
  const normalized = store.serializerFor(modelName).normalizeForPush(modelClass, responseObject);
  // getting LID from teh recordIdentifier allows Ember Data to match this record
  // with existing record in the store.
  const identifier = recordIdentifierFor(record);
  normalized.data.lid = identifier.lid;
  store.push(normalized);

  // if the server returns image membership attributes in a different order from the order they're put,
  // the data in the record's fragments will be swapped around. There seems to be a bug in `didSaveRecord`
  // that will cause ordinal in the fragment's `recordData` to become out of sync with the `ordinal` returned
  // by the model. Calling `notifyPropertyChange` syncs the value exposed by the model to the value on the
  // internal model's record data object.
  record.imageMemberships?.forEach(imageMembership => {
    imageMembership.notifyPropertyChange('ordinal');
  });

  // pushing the response data doesn't actually "clean" the record, any locally changed attributes
  // that are different from the server response will still be considered dirty. However, in this case we just saved
  // the record's data so if any of the attributes are different it's because the server overwrote them.
  // We rollback to get the model back to a clean state with only the server data.
  record.rollbackAttributes(false);
}

/*
  notifying the store that a record has been remotely deleted and should be fully removed.
*/
function pushDeletion(store, type, id) {
  const record = store.peekRecord(type, id);
  if (record !== null) {
    const relationships = {};
    let hasRelationships = false;
    record.eachRelationship((name, {
      kind
    }) => {
      hasRelationships = true;
      relationships[name] = {
        data: kind === 'hasMany' ? [] : null
      };
    });
    if (hasRelationships) {
      store.push({
        data: {
          type,
          id,
          relationships
        }
      });
    }
    record.unloadRecord();
  }
}

// eslint-disable-next-line ember/no-classic-classes
export default Adapter.extend({
  init(args) {
    this._super(args);
    this.useFetch = false;
  },
  catalog: service(),
  store: service(),
  hasFetchedAll: false,
  shouldBackgroundReloadAll() {
    return false;
  },
  shouldReloadAll() {
    return !this.hasFetchedAll;
  },
  /*
  Finds a single Catalog Object with the internal Merchant Catalog API `Get` endpoint.
   Additional parameters can be passed as `adapterOptions` to control fetching of related objects.
  Example:
    `this.store.find('item', id, { adapterOptions: { includedRelationships: ['itemVariations'] } });`
   Possible params to adapterOptions:
    [includedRelationships] - array of relationships to automatically fetch for the record
      There are two allowed formats for entries in this array:
        1. a string relationship key, i.e. 'itemVariations'
        2. a JS Object with `key` and `includedRelationships` attributes, i.e. { key: 'itemVariations', includedRelationships: ['itemVariationVendorInfos'] }
       The object format can be used to recursively fetch nested relationships more than one step away from the original model
  */
  async findRecord(store, type, id, {
    adapterOptions = {}
  }) {
    const object = await this.catalog.getObjectById(type.catalogType(), id);
    if (!object || object.catalog_type !== type.catalogType()) {
      throw new CatalogError({
        catalogErrorType: CatalogErrorType.OBJECT_NOT_FOUND,
        message: `Request for object of type ${type.catalogType()} with token ${id} returned no object or an object of the wrong catalog type.`,
        badObject: object
      });
    }
    if (object) {
      object.relatedObjects = await this._fetchRelatedObjects(type, [object], adapterOptions.includedRelationships);
    }
    return object;
  },
  async findMany(store, type, ids, snapshots) {
    const objects = await this.catalog.getObjectsById(ids);
    if (!isEmpty(objects)) {
      // note(cclegg): This assumes that all the requests asked for the same related objects. I feel like this will
      // mostly always be the case, but if it is not in the future we can update this to fetch different related objects
      // for each object.
      const includedRelationships = A(snapshots).firstObject.adapterOptions?.includedRelationships;
      A(objects).firstObject.relatedObjects = await this._fetchRelatedObjects(type, objects, includedRelationships);
    }
    return objects;
  },
  /*
  Finds all Catalog Objects of a particular type with the internal Merchant Catalog API `Get` endpoint.
   This function uses a paged API internally, but will continue fetching pages until all objects have been received.
   This operation will fail for certain object types that should only be accessed with paged APIs. Attempting to fetch
  all objects of these types will fail for merchants with large catalogs. See _verifyFetchAllIsAllowed for details.
   Additional parameters can be passed as `adapterOptions` to control fetching of related objects.
  Example:
    `this.store.findAll('item', { adapterOptions: { includedRelationships: ['itemVariations'] } });`
   Possible params to adapterOptions:
    [includedRelationships] - array of relationships to automatically fetch for the record
      There are two allowed formats for entries in this array:
        1. a string relationship key, i.e. 'itemVariations'
        2. a JS Object with `key` and `includedRelationships` attributes, i.e. { key: 'itemVariations', includedRelationships: ['itemVariationVendorInfos'] }
       The object format can be used to recursively fetch nested relationships more than one step away from the original model
  */
  async findAll(store, type, neverSet, {
    adapterOptions = {}
  }) {
    this._verifyFetchAllIsAllowed(type, adapterOptions.ignorePagingWarning);
    const objects = await this.catalog.getObjects({
      catalogTypes: [type.catalogType()],
      sortKey: type.sortKey,
      skipCatalogTypeFilterForTypes: [type.catalogType()]
    });
    if (objects && objects.length > 0) {
      // The relatedObjects attached to returned objects will be sideloaded by the store.
      // It isn't important for them to be specifically linked to their related objects
      // so we add them all to the first object here for simplicity.
      A(objects).firstObject.relatedObjects = await this._fetchRelatedObjects(type, objects, adapterOptions.includedRelationships);
    }
    this.set('hasFetchedAll', true);
    return objects;
  },
  /*
  Finds all objects of a particular type that match the passed query.
   NOTE: There is a custom `search` endpoint available for doing certain more complex queries
  for items and their related objects. See the item adapter `query` documentation for details.
   Query parameters:
    [ids] - array of catalog object tokens to fetch the data for
    [text] - string query that will be matched using word-prefix matching against all string fields on objects
    [unitTokens] - array of merchant subunit tokens; will limit results to objects enabled at one or more of these locations
    [attributeQueries] - array of objects with more parameters for more specific object queries:
      Each object in this array has a `type` property that maps to query types supported by the MerchantCatalog
      `Get` endpoint (https://prototype.sqprod.co/#/docs/squareup.items.merchant.Query.Type)
       Currently supported types with examples:
        EXACT - { type: CATALOG_SERVICE_QUERY_TYPES.EXACT, attribute: 'discount_type', value: DiscountType.FIXED }
        SET - { type: CATALOG_SERVICE_QUERY_TYPES.SET, attribute: 'menu_group_membership_parent_menu_group_id', value: ['TOKEN1', 'TOKEN2'] }
        RANGE - { type: CATALOG_SERVICE_QUERY_TYPES.RANGE, attribute: 'price.cents', begin: 0, end: 1000 }
        ATTRIBUTE_VALUES - {
          type: CATALOG_SERVICE_QUERY_TYPES.ATTRIBUTE_VALUES,
          attributeDefinitionTokens: ['SQ_3', 'SQ_6'],
          value: 'Some Text'
        }
       Other query types could be added here.
   Related object parameters:
    [includedRelationships] - array of relationships to automatically fetch for the record
      There are two allowed formats for entries in this array:
        1. a string relationship key, i.e. 'itemVariations'
        2. a JS Object with `key` and `includedRelationships` attributes, i.e. { key: 'itemVariations', includedRelationships: ['itemVariationVendorInfos'] }
       The object format can be used to recursively fetch nested relationships more than one step away from the original model
   Paging parameters:
    [pageSize=20] - number of results to fetch
    [paginationToken] - token returned from last query with same parameters, used to fetch additional results
    [fetchAll=false] - enable to fetch ALL objects in the merchants catalog that match result, not available for certain object types
   Example:
    `this.store.query('item', {
      includedRelationships: ['itemVariations'],
      attributeQueries: [{
        type: CATALOG_SERVICE_QUERY_TYPES.ATTRIBUTE_VALUES,
        attributeDefinitionTokens: ['SQ_3', 'SQ_6'],
        value: 'Some Text'
      }],
      pageSize: 5
    });`
  */
  async query(store, type, {
    ids = null,
    text = null,
    unitTokens = null,
    attributeQueries = null,
    includedRelationships = [],
    pageSize = null,
    paginationToken = null,
    fetchAll = false,
    ignorePagingWarning = false,
    sortAttribute = null,
    includeObjectsMissingAttribute = false
  }) {
    const catalogTypes = [type.catalogType()];
    const sortKey = sortAttribute ?? type.sortKey;
    if (fetchAll) {
      this._verifyFetchAllIsAllowed(type, ignorePagingWarning);
    } else if (!ids) {
      // paging is enabled by default for all queries except for a set of specific ids
      pageSize = pageSize ?? DEFAULT_QUERY_PAGE_SIZE;
      paginationToken = paginationToken ?? '';
    }
    const queryParams = this.catalog.createObjectQuery({
      catalogTypes,
      pageSize,
      paginationToken,
      sortKey,
      unitTokens,
      skipCatalogTypeFilterForTypes: [type.catalogType()],
      includeObjectsMissingAttribute
    });
    if (text) {
      this.catalog.addFilterByText(queryParams, text);
    }
    if (ids) {
      if (ids.length === 0) {
        return [];
      }
      this.catalog.addFilterByTokens(queryParams, ids);
    }
    if (attributeQueries) {
      attributeQueries.forEach(query => {
        switch (query.type) {
          case CATALOG_SERVICE_QUERY_TYPES.EXACT:
          case CATALOG_SERVICE_QUERY_TYPES.SET:
            this.catalog.addFilterByAttribute(queryParams, query.attribute, query.value);
            break;
          case CATALOG_SERVICE_QUERY_TYPES.RANGE:
            this.catalog.addFilterByAttributeRange(queryParams, query.attribute, query.begin, query.end);
            break;
          case CATALOG_SERVICE_QUERY_TYPES.ATTRIBUTE_VALUES:
            this.catalog.addFilterByAttributeValues(queryParams, query.attributeValues);
            break;
          default:
            assert(`Each query in "attributeQueries" must have a valid query type. Currently supported values
              are [EXACT, SET, RANGE, ATTRIBUTE_VALUES]. See documentation for "query" in items-v3-base adapter.
              Visit #catalog-dashboard for more info.`);
        }
      });
    }
    const objects = await this.catalog.runObjectQuery(queryParams);
    const nextPageToken = objects.paginationToken;
    if (!isEmpty(objects)) {
      // The relatedObjects attached to returned objects will be sideloaded by the store.
      // It isn't important for them to be specifically linked to their related objects
      // so we add them all to the first object here for simplicity.
      A(objects).firstObject.relatedObjects = await this._fetchRelatedObjects(type, objects, includedRelationships);
    }

    // Attach extra fields after any filtering
    objects.meta = {
      nextPageToken
    };
    return objects;
  },
  async _fetchNonV3Relationship(type, ids) {
    // Split the request into separate batch-retrieve if ids exceed the PUT_BATCH_LIMIT = 1000
    if (ids.length > PUT_BATCH_LIMIT) {
      for (let i = 0; i < ids.length; i += PUT_BATCH_LIMIT) {
        await all(ids.slice(i, Math.min(ids.length, i + PUT_BATCH_LIMIT)).map(id => this.store.findRecord(type, id)));
      }
      return [];
    } else {
      return all(ids.map(id => this.store.findRecord(type, id)));
    }
  },
  async _fetchRelatedObjects(type, objects, includedRelationships = []) {
    const inverseHasManyKeys = {};
    const objectTokens = [];
    const nestedIncludedRelationships = new Map();
    const nonV3Promises = [];
    includedRelationships.forEach(includedRelationship => {
      let relationshipName = includedRelationship;
      if (typeof includedRelationship === 'object') {
        relationshipName = includedRelationship.key;
        nestedIncludedRelationships.set(relationshipName, includedRelationship.includedRelationships);
      }
      const relationship = type.relationshipsByName.get(relationshipName);

      // check if included relationship is a fragment array of memberships
      if (!relationship) {
        const attribute = type.attributes.get(relationshipName);
        if (!attribute?.isFragment || !attribute.options?.relatedType) {
          assert(`Unable to find relationship with name ${relationshipName} for type ${type.modelName}. Passed types should be the relationship key for a hasMany or belongsTo relationship, or a key for a fragmentArray representing members to a relatedType (see object-image-membership)`);
        }
        const key = underscore(relationshipName);
        const ids = objects.reduce((ids, object) => {
          const objectIds = object[key]?.mapBy('string_value').compact() || [];
          ids.push(...objectIds);
          return ids;
        }, []).uniq();
        if (!isEmpty(ids)) {
          nonV3Promises.push(this._fetchNonV3Relationship(attribute.options?.relatedType, ids));
        }
        return;
      }
      const relatedType = this.store.modelFor(relationship.type);

      // handle non-v3 relationships
      if (isNone(relatedType.catalogType)) {
        const key = this._keyForRelationship(relationship.name, relationship.kind, type.modelName);
        const ids = objects.reduce((ids, object) => {
          if (object[key]) {
            return [...ids, ...(relationship.kind === 'hasMany' ? object[key] : [object[key]])];
          }
          return ids;
        }, []).uniq();
        nonV3Promises.push(this._fetchNonV3Relationship(relationship.type, ids));
        return;
      }

      // handle items v3 relationships
      if (relationship.kind === 'hasMany') {
        const relatedCatalogType = relatedType.catalogType();
        const inverseRelationship = type.inverseFor(relationshipName, this.store);
        if (inverseRelationship) {
          inverseHasManyKeys[relatedCatalogType] = this._keyForRelationship(inverseRelationship.name, inverseRelationship.kind, inverseRelationship.type.modelName);
        } else {
          const key = this._keyForRelationship(relationshipName, relationship.kind, type.modelName);
          objects.forEach(object => {
            if (object[key]) {
              objectTokens.push(...object[key].toArray());
            }
          });
        }
      } else if (relationship.kind === 'belongsTo') {
        const key = this._keyForRelationship(relationshipName, relationship.kind, type.modelName);
        objects.forEach(object => {
          if (object[key]) {
            objectTokens.push(object[key]);
          }
        });
      }
    });

    // Use neighbor queries to fetch hasMany related objects
    const neighborObjectPromises = [];
    if (Object.keys(inverseHasManyKeys).length > 0) {
      const tokens = objects.mapBy('id');
      for (let i = 0; i < tokens.length; i += QUERY_LIMIT) {
        const promise = this.catalog.getNeighborObjects(tokens.slice(i, Math.min(tokens.length, i + QUERY_LIMIT)), inverseHasManyKeys);
        neighborObjectPromises.addObject(promise);
      }
    }

    // Use id queries to fetch belongsTo queries and repeated hasMany relationships
    const objectsByIdPromises = [];
    const objectIds = A(objectTokens).uniq();
    for (let i = 0; i < objectIds.length; i += QUERY_LIMIT) {
      const promise = this.catalog.getObjectsById(objectIds.slice(i, Math.min(objectIds.length, i + QUERY_LIMIT)));
      A(objectsByIdPromises).addObject(promise);
    }

    // Wait for all requests and combine results
    const {
      v3responses
    } = await hash({
      v3responses: all([...objectsByIdPromises, ...neighborObjectPromises]),
      nonV3Responses: all(nonV3Promises)
    });
    const originalObjectIds = objects.reduce((ids, object) => ids.set(object.id, true), new Map());
    const allRelatedObjects = v3responses.reduce((allObjects, responseObjects) => {
      const relatedObjects = A(responseObjects).reject(object => originalObjectIds.has(object.id));
      return [...allObjects, ...relatedObjects];
    }, []);

    // Fetch deeper nested relationships if requested
    if (nestedIncludedRelationships.size > 0) {
      const relatedObjectsByType = this._groupByCatalogType(allRelatedObjects);
      const recursiveRelatedObjectPromises = [];
      nestedIncludedRelationships.forEach((includedRelationships, relationshipName) => {
        const relationship = type.relationshipsByName.get(relationshipName);
        const relatedType = this.store.modelFor(relationship.type);
        const relatedObjectsOfType = relatedObjectsByType[CATALOG_SERVICE_TYPES[relatedType.catalogType()]];
        if (relatedObjectsOfType) {
          recursiveRelatedObjectPromises.push(this._fetchRelatedObjects(relatedType, relatedObjectsOfType, includedRelationships));
        }
      });
      const recursiveResponses = await all(recursiveRelatedObjectPromises);
      recursiveResponses.forEach(recursiveRelatedObjects => {
        allRelatedObjects.push(...recursiveRelatedObjects);
      });
    }
    return allRelatedObjects;
  },
  _groupByCatalogType(objects) {
    return objects.reduce((objectsByType, object) => {
      const catalogType = CATALOG_SERVICE_TYPES[object.catalog_type];
      if (isNone(objectsByType[catalogType])) {
        objectsByType[catalogType] = [];
      }
      objectsByType[catalogType].push(object);
      return objectsByType;
    }, {});
  },
  _keyForRelationship(key, typeClass, relationshipModelName) {
    return this.store.serializerFor(relationshipModelName).keyForRelationship(key, typeClass);
  },
  _modelNameForCatalogType(catalogType) {
    return this.store.serializerFor('items-v3-base')._modelNameForCatalogType(catalogType);
  },
  /*
  The goal of this check is to prevent new UIs from being built in a non-scalable way because an engineer/team
  is unaware of the performance implications of trying to prefetch large portions of a merchant's catalog.
  Prefetching seems like an attractive approach for many teams because it simplifies the application code after
  loading in the route, but we should be focused on creating scalable features that work well for all merchants,
  including those with large catalogs.
   This warning can be ignored for legacy use cases or for future cases that choose not to prioritize scalability.
   Example disabling for "findAll":
    `this.store.findAll('item', { adapterOptions: { ignorePagingWarning: true }});`
   Example disabling for "query":
    `this.store.query('item', { fetchAll: true, ignorePagingWarning: true });`
  */
  _verifyFetchAllIsAllowed(type, ignorePagingWarning) {
    if (FETCH_ALL_DISALLOWED_FOR_TYPE[type.catalogType()]) {
      assert(`fetching all models of type ${type.modelName} is strongly discouraged. The number of objects of
        this type in a merchant's catalog is unbounded and often extremely large in practice, meaning attempts to
        fetch all objects for a merchant can take multiple minutes or fail completely for merchants with large catalogs.
        All new features utilizing this model should be implemented in a scalable way by fetching data in pages or
        with limited queries. Visit #catalog-dashboard for assistance.`, ignorePagingWarning);
    }
  },
  async createRecord(store, type, snapshot) {
    const options = {
      ignoreItemDefaults: snapshot.adapterOptions?.ignoreItemDefaults
    };
    const responseRecords = await this._processRecords([snapshot.record], options);
    const originalRecordResponse = responseRecords[this.primaryObjectIndex];
    return originalRecordResponse;
  },
  async updateRecord(store, type, {
    record
  }) {
    const responseRecords = await this._processRecords([record]);
    const originalRecordResponse = responseRecords.find(r => r.id === record.id);
    return originalRecordResponse;
  },
  deleteRecord(store, type, {
    record
  }) {
    return this._processRecords([record], {
      isDelete: true
    });
  },
  batchRecords(records) {
    return this._processRecords(records);
  },
  async _processRecords(records, options = {}) {
    const recordsToProcess = this._expandRelationships(records);
    const {
      created,
      updated,
      deleted
    } = recordsToProcess;
    const batches = [];
    for (let i = 0; i < deleted.length; i += DELETE_BATCH_LIMIT) {
      batches.push({
        deleteRecords: deleted.slice(i, Math.min(deleted.length, i + DELETE_BATCH_LIMIT))
      });
    }

    // if we get to this point by calling one of the delete functions, then we don't want to save changes to dirty/new related objects
    if (!options.isDelete) {
      const saves = [...created, ...updated];
      for (let i = 0; i < saves.length; i += PUT_BATCH_LIMIT) {
        const saveRecords = saves.slice(i, Math.min(saves.length, i + PUT_BATCH_LIMIT));
        if (i < batches.length) {
          batches[i].saveRecords = saveRecords;
        } else {
          batches.push({
            saveRecords
          });
        }
      }
    }
    try {
      const batchResponses = [];
      const batchRecordsInternalOptions = {
        ignoreItemDefaults: options.ignoreItemDefaults
      };
      for (const batch of batches) {
        const responseRecords = await this._batchRecordsInternal(batch, batchRecordsInternalOptions);
        batchResponses.push(...responseRecords);
      }
      return batchResponses;
    } catch (error) {
      Logger.error('CatalogAdapter._processRecords failed', error);
      if (!isNone(error.stack)) {
        Logger.error(error.stack);
      }
      throw error;
    }
  },
  _expandRelationships(records) {
    const expandedRecords = new Map();
    function processRelatedRecords(record, relationshipName, relationshipKind, nestedRelationships) {
      // if we've already destroyed the record, it's a delete
      // that has already been processed, don't expand here.
      if (record.isDestroyed) {
        return;
      }
      let relatedRecords = [];
      if (relationshipKind === 'belongsTo') {
        const relationshipReference = record.belongsTo(relationshipName);
        if (!isNone(relationshipReference.value())) {
          relatedRecords = [record[relationshipName]].filter(r => r !== null && r !== undefined && r !== '');
        }
      } else {
        const relationshipReference = record.hasMany(relationshipName);
        if (!isNone(relationshipReference.value())) {
          relatedRecords = record[relationshipName].toArray();
        }
      }
      const nonPlaceholderRecords = relatedRecords.filter(r => !r.isPlaceholder);
      nonPlaceholderRecords.forEach(relatedRecord => {
        // Add dirty related records to expanded list that are one step away
        if (relatedRecord.isDirty) {
          expandedRecords.set(relatedRecord, relatedRecord.dirtyType || 'updated'); // if record is only "dirty" because of related records we default to "updated"
        }

        // Look for requested nested records that are two steps away
        if (!relatedRecord.get('isDeleted') && isPresent(nestedRelationships)) {
          nestedRelationships.forEach(nestedRelationship => {
            const relationship = relatedRecord.constructor.relationshipsByName.get(nestedRelationship);
            const nestedRelationships = relationship && relationship.options ? relationship.options.autosaved : null;
            processRelatedRecords(relatedRecord, nestedRelationship, relationship.kind, nestedRelationships);
          });
        }
      });
    }
    records.forEach(record => {
      // Ember Data does not mark a record "dirty" because of relationships, so we do not
      // check for dirty here as we did in the Ember Model adapter.
      // if record is only "dirty" because of related records we default to "updated"
      expandedRecords.set(record, record.dirtyType || 'updated');
      record.eachRelationship((name, descriptor) => {
        if (this.store.modelFor(descriptor.type).dataSource === 'CATALOG') {
          processRelatedRecords(record, name, descriptor.kind, descriptor.options.autosaved);
        }
      });
    });
    const groups = {
      created: [],
      updated: [],
      deleted: []
    };
    expandedRecords.forEach((dirtyType, record) => {
      groups[dirtyType].push(record);
    });
    return groups;
  },
  async _batchRecordsInternal({
    saveRecords = [],
    deleteRecords = []
  }, options) {
    const creates = saveRecords.filter(r => r.isNew);
    const updates = saveRecords.filter(r => !r.isNew);
    const saves = [...creates, ...updates];
    let primaryObject;
    if (saves.length > 0) {
      primaryObject = saves[0];
    }
    saves.sort((recordA, recordB) => {
      const aType = recordA.constructor.catalogType();
      const bType = recordB.constructor.catalogType();
      const recordAPriority = sortObjectPriorities.includes(aType) ? sortObjectPriorities.indexOf(aType) : Number.POSITIVE_INFINITY;
      const recordBPriority = sortObjectPriorities.includes(bType) ? sortObjectPriorities.indexOf(bType) : Number.POSITIVE_INFINITY;
      return recordAPriority - recordBPriority;
    });
    if (primaryObject) {
      this.set('primaryObjectIndex', saves.indexOf(primaryObject));
    }
    const putRecords = saves.map(record => record.serialize());
    let batchResponse;
    try {
      batchResponse = await this.catalog.batch({
        putObjects: putRecords,
        deleteObjects: deleteRecords
      }, options);
    } catch (error) {
      // Determine if this is an error we can log some additional context about (because we have
      // the original model object that failed)
      if (isPresent(error.catalogErrorType) && error.catalogErrorType === CatalogErrorType.BAD_REQUEST && isPresent(error.badObject)) {
        const modelForObject = saves.find(s => s.id === error.badObject.token);
        Logger.error('CatalogAdapter.batchRecords: bad request\nfor model:', modelForObject && modelForObject.toJSON(), '\nand serialized request object:', error.badObject);
      }
      throw error;
    }

    // NOTE(cclegg): When converting to EmberData I stripped out some logic from this response handler that
    // I believed to now be unneccessary because of how EmberData handles relationships. If issues occur check
    // _createOrUpdateRecordsHandlePutResponse in the old `catalog-adapter` to see which parts were cut.

    const savedResponseObjects = batchResponse.putRecords;
    for (const [i, presaveRecord] of saves.entries()) {
      const responseObject = savedResponseObjects[i];
      const unitTokens = new Set(Object.keys(presaveRecord.unitOverrides), Object.keys(responseObject.unit_overrides));

      // with stock actions we save the item variations before we send inventory changes to inventory2.
      // therefore we need to ignore the stock count and sold out attributes returned by itemsfe (which won't have the stock actions)
      // and use the locally updated stock count and sold out state.
      if (responseObject.catalog_type === 'ITEM_VARIATION') {
        for (const token of unitTokens) {
          ['inventory_availability_rule', 'inventory_in_stock_count', 'fractional_inventory_in_stock_count', 'fractional_inventory_committed_count', 'fractional_inventory_available_count', 'sold_out', 'sold_out_valid_until'].forEach(attributeName => {
            if (!isNone(presaveRecord.getUnitOverride(token, attributeName))) {
              if (isNone(responseObject.unit_overrides)) {
                responseObject.unit_overrides = {};
              }
              if (isNone(responseObject.unit_overrides[token])) {
                responseObject.unit_overrides[token] = {};
              }
              responseObject.unit_overrides[token][attributeName] = presaveRecord.getUnitOverride(token, attributeName);
            }
          });
        }
      }
      if (responseObject.catalog_type === 'ITEM_MODIFIER_OPTION') {
        for (const token of unitTokens) {
          ['sold_out', 'sold_out_valid_until'].forEach(attributeName => {
            if (!isNone(presaveRecord.getUnitOverride(token, attributeName))) {
              if (isNone(responseObject.unit_overrides)) {
                responseObject.unit_overrides = {};
              }
              if (isNone(responseObject.unit_overrides[token])) {
                responseObject.unit_overrides[token] = {};
              }
              responseObject.unit_overrides[token][attributeName] = presaveRecord.getUnitOverride(token, attributeName);
            }
          });
        }
      }
      commitSaveToRecord(this.store, this._modelNameForCatalogType(responseObject.catalog_type), presaveRecord, responseObject);
    }
    deleteRecords.forEach(record => {
      // if record is saving per ED, it has already gotten a call to `save`, and isn't
      // being processed by the batch alone. Don't try to force deletion on those objects.
      if (!record.isSaving) {
        pushDeletion(this.store, this._modelNameForCatalogType(record.constructor.catalogType()), record.id);
      }
    });

    // Since catalog models can be deleted by server-defined cascade rules,
    // we need to handle any additional models that have been deleted
    // beyond the ones we explicitly requested. `batchResponse.deleteRecords`
    // contains all of the objects that were deleted, so we filter out the ones we asked
    // the server to delete, and then unload the cascade-deleted records from the store.
    const deleteRecordIdsSet = new Set(deleteRecords.map(r => r.id));
    const cascadeDeletedRecords = batchResponse.deleteRecords.filter(recordJSON => {
      return !deleteRecordIdsSet.has(recordJSON.id);
    });
    cascadeDeletedRecords.forEach(recordJSON => {
      const modelName = this.store.serializerFor('items-v3-base')._modelNameForCatalogType(recordJSON.catalog_type);
      // NB(cclegg): The server may return info for a cascade deleted object of a type that we don't have
      // a model for on Dashboard. In that case we can skip this code because there will not be an EmberData record to unload.
      if (modelName) {
        const record = modelName ? this.store.peekRecord(modelName, recordJSON.id) : null;
        if (record) {
          record.unloadRecord();
        }
      }
    });
    return savedResponseObjects;
  },
  filterObjects({
    objects
  }) {
    return objects;
  }
});